import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { Prescription, UserProfile, Medication } from '../types';
import { FileText, Package, ShoppingCart, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'motion/react';

export default function PrescriptionView() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [doctor, setDoctor] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDF, setIsDF] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<'idle' | 'success'>('idle');

  const handlePurchase = () => {
    setPurchaseStatus('success');
    // In a real app, this would redirect to the e-commerce site
  };

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      const presDoc = await getDoc(doc(db, 'prescriptions', id));
      if (presDoc.exists()) {
        const data = presDoc.data() as Prescription;
        setPrescription(data);
        
        const docDoc = await getDoc(doc(db, 'users', data.doctorId));
        if (docDoc.exists()) setDoctor(docDoc.data() as UserProfile);
      }
      
      // Check if patient is from DF
      if (user?.cep) {
        // Simple check for DF CEP range: 70000-000 to 72799-999 and 73000-000 to 73699-999
        const cepNum = parseInt(user.cep.replace(/\D/g, ''));
        if ((cepNum >= 70000000 && cepNum <= 72799999) || (cepNum >= 73000000 && cepNum <= 73699999)) {
          setIsDF(true);
        }
      }
      
      setLoading(false);
    };

    fetchData();
  }, [id, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-text-main">Prescrição não encontrada.</h2>
        <Link to="/dashboard" className="mt-4 text-primary hover:underline">Voltar ao Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <Link to="/dashboard" className="inline-flex items-center text-text-muted hover:text-text-main mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao Dashboard
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl border border-text-muted/10 overflow-hidden"
      >
        {purchaseStatus === 'success' && (
          <div className="p-4 bg-emerald-100 border-b border-emerald-200 text-emerald-800 flex items-center justify-center font-bold">
            <CheckCircle2 className="w-5 h-5 mr-2" /> Redirecionando para o e-commerce da Novità...
          </div>
        )}
        <div className="p-8 border-b border-text-muted/10 bg-bg-light flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center">
              <FileText className="w-6 h-6 mr-3 text-primary" /> Receita Médica Digital
            </h1>
            <p className="text-text-muted mt-1">Emitida em {new Date(prescription.createdAt).toLocaleDateString('pt-BR')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-text-main">{doctor?.name}</p>
            <p className="text-xs text-text-muted">CRM: {doctor?.specialty || 'Médico'}</p>
          </div>
        </div>

        <div className="p-8 space-y-8">
          <div>
            <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Medicamentos Prescritos</h2>
            <div className="space-y-6">
              {prescription.medications.map((med, i) => (
                <div key={i} className="flex items-start p-4 bg-bg-light rounded-xl border border-text-muted/10">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4 shadow-sm">
                    <Package className="w-5 h-5 text-secondary" />
                  </div>
                  <div className="flex-grow">
                    <p className="text-lg font-bold text-text-main">{med.name}</p>
                    <p className="text-sm text-secondary-dark font-medium">{med.dosage}</p>
                    <p className="text-sm text-text-muted mt-2 italic">{med.instructions}</p>
                  </div>
                  {isDF && (
                    <button className="ml-4 p-2 bg-primary text-white rounded-lg hover:bg-primary-dark shadow-md transition-all">
                      <ShoppingCart className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="pt-8 border-t border-text-muted/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center text-secondary-dark font-bold">
              <CheckCircle2 className="w-6 h-6 mr-2" />
              <span>Assinado Digitalmente via ICP-Brasil</span>
            </div>
            
            {isDF && (
              <button
                onClick={handlePurchase}
                className="inline-flex items-center px-8 py-4 bg-secondary text-white font-bold rounded-xl hover:bg-secondary-dark shadow-lg transition-all transform hover:scale-105"
              >
                <ShoppingCart className="w-5 h-5 mr-2" /> Comprar Medicamentos (Entrega no DF)
              </button>
            )}
          </div>
        </div>
      </motion.div>
      
      {!isDF && (
        <div className="mt-8 p-4 bg-bg-light rounded-xl border border-text-muted/10 flex items-start">
          <AlertCircle className="w-5 h-5 text-secondary mr-3 mt-0.5" />
          <p className="text-sm text-text-main/70">
            O programa "Medicamento em Casa" com entrega rápida está disponível apenas para o Distrito Federal no momento.
            Em breve expandiremos para sua região!
          </p>
        </div>
      )}
    </div>
  );
}
